import { Fleet, Vehicle } from '../../components/map/components/FleetMenu';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';

/**
 * Fleet KML Generator - Handles conversion of fleet data to/from KML format
 * Integrates fleet data into the existing map saving system
 */

/**
 * Serialize fleet data to KML-compatible format
 * @param fleets Array of fleet objects to serialize
 * @returns KML fragment containing fleet data in ExtendedData
 */
export function serializeFleetToKml(fleets: Fleet[]): string {
  if (fleets.length === 0) return '';
  
  // Optimize fleet data for storage by removing UI-specific fields
  const optimizedFleets = optimizeFleetForStorage(fleets);
  
  // Convert to JSON and encode for KML storage
  const fleetData = JSON.stringify(optimizedFleets, null, 2);
  const encodedData = encodeURIComponent(fleetData);
  
  return `
    <Folder>
      <name>Fleet Configuration</name>
      <ExtendedData>
        <Data name="fleet_data">
          <value>${encodedData}</value>
        </Data>
        <Data name="fleet_version">
          <value>2.0</value>
        </Data>
      </ExtendedData>
    </Folder>
  `;
}

/**
 * Deserialize fleet data from KML format
 * @param kmlData KML string containing fleet data
 * @returns Array of fleet objects or empty array if no fleet data found
 */
export function deserializeFleetFromKml(kmlData: string): Fleet[] {
  try {
    // Extract fleet ExtendedData from KML
    const fleetElement = extractFleetElement(kmlData);
    if (!fleetElement) return [];
    
    // Decode and parse JSON data
    const encodedData = extractFleetJson(fleetElement);
    const decodedData = decodeURIComponent(encodedData);
    const fleetData = JSON.parse(decodedData);
    
    // Validate and return fleet data
    return validateFleetData(fleetData);
  } catch (error) {
    console.warn('Failed to parse fleet data:', error);
    return [];
  }
}

/**
 * Extract fleet folder element from KML data
 * @param kmlData KML string to search
 * @returns Fleet folder element or null if not found
 */
function extractFleetElement(kmlData: string): Element | null {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlData, 'text/xml');
  
  // Look for folder with name "Fleet Configuration"
  const folders = xml.querySelectorAll('Folder');
  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i];
    const nameElement = folder.querySelector('name');
    if (nameElement?.textContent === 'Fleet Configuration') {
      return folder;
    }
  }
  
  return null;
}

/**
 * Extract fleet JSON data from fleet element
 * @param fleetElement Fleet folder element
 * @returns Encoded fleet JSON string
 */
function extractFleetJson(fleetElement: Element): string {
  const dataElements = fleetElement.querySelectorAll('ExtendedData Data');
  for (let i = 0; i < dataElements.length; i++) {
    const dataElement = dataElements[i];
    const nameAttr = dataElement.getAttribute('name');
    if (nameAttr === 'fleet_data') {
      const valueElement = dataElement.querySelector('value');
      return valueElement?.textContent || '';
    }
  }
  return '';
}

/**
 * Validate fleet data structure
 * @param data Parsed fleet data
 * @returns Validated fleet array
 */
function validateFleetData(data: any): Fleet[] {
  // Ensure data is an array
  if (!Array.isArray(data)) {
    console.warn('Fleet data is not an array:', data);
    return [];
  }
  
  // Validate each fleet
  return data.filter(fleet => {
    if (!fleet || typeof fleet !== 'object') {
      console.warn('Invalid fleet object:', fleet);
      return false;
    }
    
    if (!fleet.id || !fleet.name || !Array.isArray(fleet.vehicles)) {
      console.warn('Fleet missing required fields:', fleet);
      return false;
    }
    
    // Validate vehicles
    const validVehicles = fleet.vehicles.filter((vehicle: any) => {
      if (!vehicle || typeof vehicle !== 'object') {
        console.warn('Invalid vehicle object:', vehicle);
        return false;
      }
      
      const requiredFields = ['id', 'name', 'standardSeats', 'wheelchairSeats', 'startLocation', 'endLocation', 'operatingHours'];
      for (const field of requiredFields) {
        if (!(field in vehicle)) {
          console.warn(`Vehicle missing required field '${field}':`, vehicle);
          return false;
        }
      }
      
      if (!Array.isArray(vehicle.operatingHours)) {
        console.warn('Vehicle operatingHours is not an array:', vehicle);
        return false;
      }
      
      return true;
    });
    
    return validVehicles.length > 0;
  });
}

/**
 * Optimize fleet data for storage by removing UI-specific fields
 * @param fleets Array of fleet objects
 * @returns Optimized fleet data for storage
 */
function optimizeFleetForStorage(fleets: Fleet[]): Fleet[] {
  return fleets.map(fleet => {
    // Persist uatMetadata with serviceDate; use DEFAULT_SIMULATION_DATE when missing for consistency
    const serviceDate = fleet.uatMetadata?.serviceDate || DEFAULT_SIMULATION_DATE;
    const uatMetadata = {
      ...fleet.uatMetadata,
      serviceDate: serviceDate.split('T')[0]  // Ensure YYYY-MM-DD format
    };
    return {
      id: fleet.id,
      name: fleet.name,
      uatMetadata,
      vehicles: fleet.vehicles.map(vehicle => ({
        id: vehicle.id,
        name: vehicle.name,
        standardSeats: vehicle.standardSeats,
        wheelchairSeats: vehicle.wheelchairSeats,
        startLocation: vehicle.startLocation,
        endLocation: vehicle.endLocation,
        operatingHours: vehicle.operatingHours,
        numberOfVehicles: vehicle.numberOfVehicles,
        ...(vehicle.uatMetadata && { uatMetadata: { ...vehicle.uatMetadata, serviceDate: (vehicle.uatMetadata.serviceDate || serviceDate).split('T')[0] } })
      }))
    };
  });
}

/**
 * Combine layer KML and fleet KML into a single KML document
 * @param layerKml KML string for map layers
 * @param fleetKml KML string for fleet data
 * @returns Combined KML string
 */
export function combineKmlData(layerKml: string, fleetKml: string): string {
  if (!fleetKml.trim()) {
    // No fleet data, return layer KML as-is
    return layerKml;
  }
  
  // Insert fleet KML before the closing Document tag
  const documentEndIndex = layerKml.lastIndexOf('</Document>');
  if (documentEndIndex === -1) {
    console.warn('Could not find </Document> tag in layer KML');
    return layerKml;
  }
  
  return layerKml.slice(0, documentEndIndex) + fleetKml + layerKml.slice(documentEndIndex);
} 